import { Button, Typography } from '@mui/material';
import { useMemo } from 'react';
import { useFieldArray, useWatch } from 'react-hook-form';

import { Icon } from '@/components/Icon/Icon';

import { useTranslate } from '@/hooks/useTranslate';

import { getDataTestId } from '@/utils/browser';

import { ForeignTaxResidencySectionProps } from '../TaxInfoDrawer.interface';
import { getForeignTaxResidencyCountryOptions } from '../utils/countryOptions';
import { MAX_FOREIGN_TAX_RESIDENCIES } from './constants';
import { ForeignTaxResidencyBlock } from './ForeignTaxResidencyBlock';
import { DEFAULT_FOREIGN_TAX_RESIDENCY_BLOCK } from './utils';

import {
  TaxInfoAddBlockRow,
  TaxInfoQuestionLabel,
  TaxInfoSubSection,
} from '../TaxInfoDrawer.styled';

export function ForeignTaxResidencySection({
  control,
  errors,
  isPending,
  resetField,
  tenantCountryCode,
}: Readonly<ForeignTaxResidencySectionProps>) {
  const { t, country: translatedCountry } = useTranslate();

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'foreignTaxResidencies',
  });

  const watchedResidencies = useWatch({
    control,
    name: 'foreignTaxResidencies',
  });

  const countryOptions = useMemo(
    () =>
      getForeignTaxResidencyCountryOptions(
        tenantCountryCode,
        translatedCountry,
      ),
    [tenantCountryCode, translatedCountry],
  );
  const handleAddBlock = () => {
    if (fields.length < MAX_FOREIGN_TAX_RESIDENCIES) {
      append({ ...DEFAULT_FOREIGN_TAX_RESIDENCY_BLOCK });
    }
  };

  return (
    <TaxInfoSubSection>
      <TaxInfoQuestionLabel variant="body1Medium">
        {t('taxInfoDrawer.question2.foreignTaxResidency.title')}
      </TaxInfoQuestionLabel>
      <Typography variant="body2" color="text.secondary" textAlign="center">
        {t('taxInfoDrawer.question2.foreignTaxResidency.subtitle', {
          count: MAX_FOREIGN_TAX_RESIDENCIES,
        })}
      </Typography>

      {fields.map((field, index) => {
        const selectedByOthers = new Set(
          (watchedResidencies ?? [])
            .map((block, blockIndex) =>
              blockIndex === index ? undefined : block?.countryCode,
            )
            .filter((countryCode): countryCode is string => !!countryCode),
        );

        const availableCountryOptions = countryOptions.filter(
          option => !selectedByOthers.has(String(option.value)),
        );

        return (
          <ForeignTaxResidencyBlock
            key={field.id}
            index={index}
            total={fields.length}
            control={control}
            errors={errors}
            countryOptions={availableCountryOptions}
            isPending={isPending}
            resetField={resetField}
            onDelete={() => remove(index)}
          />
        );
      })}

      {fields.length < MAX_FOREIGN_TAX_RESIDENCIES && (
        <TaxInfoAddBlockRow>
          <Button
            variant="text"
            startIcon={<Icon name="plus" />}
            onClick={handleAddBlock}
            disabled={isPending}
            {...getDataTestId('tax-info-foreign-tax-residency-add-country')}
          >
            {t('taxInfoDrawer.question2.foreignTaxResidency.addCountry')}
          </Button>
        </TaxInfoAddBlockRow>
      )}
    </TaxInfoSubSection>
  );
}
